import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { formatCountdown, formatDropTime } from "../../shared/i18n/index.ts";
import { bucket, type Segment } from "../../shared/price.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { PriceDisplay } from "../components/PriceDisplay.tsx";
import { api } from "../lib/api.ts";
import { serverNow, syncClock, tickStagger } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";
import { ownerSegment, type OwnerRow } from "../lib/owner.ts";

type Tab = "active" | "scheduled" | "draft" | "ended";
const tabs: {
  key: Tab;
  label: "sell.tabs.active" | "sell.tabs.scheduled" | "sell.tabs.drafts" | "sell.tabs.ended";
}[] = [
  { key: "active", label: "sell.tabs.active" },
  { key: "scheduled", label: "sell.tabs.scheduled" },
  { key: "draft", label: "sell.tabs.drafts" },
  { key: "ended", label: "sell.tabs.ended" },
];

/** /me/listings — seller dashboard rows (R-LST-9, 08-design §My listings). */
export function MyListings() {
  const { t, locale } = useI18n();
  const { me, loading } = useMe();
  const [params, setParams] = useSearchParams();
  const tab = (tabs.find((x) => x.key === params.get("status"))?.key ?? "active") as Tab;
  const [rows, setRows] = useState<OwnerRow[] | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!me?.can_sell) return;
    let live = true;
    const load = () =>
      api<{ items: OwnerRow[]; server_ts: number }>(`/api/me/listings?status=${tab}`)
        .then((r) => {
          if (!live) return;
          syncClock(r.server_ts);
          setRows(r.items);
        })
        .catch(() => live && setRows([]));
    void load();
    const id = setInterval(load, 30_000);
    const tick = setInterval(() => setTick((n) => n + 1), 1000);
    return () => {
      live = false;
      clearInterval(id);
      clearInterval(tick);
    };
  }, [me?.can_sell, tab]);

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to="/login?next=/me/listings" replace />;
  if (!me.can_sell) return <Navigate to="/sell" replace />;
  const now = serverNow();

  return (
    <div className="page">
      <div className="row row--between">
        <h1 className="h1">{t("nav.sell")}</h1>
        <Link className="btn btn--accent" to="/sell" data-testid="new-listing">
          + {t("sell.title")}
        </Link>
      </div>
      <div className="chips" role="tablist">
        {tabs.map((x) => (
          <button
            key={x.key}
            role="tab"
            aria-selected={x.key === tab}
            className={`chip${x.key === tab ? " is-on" : ""}`}
            onClick={() => setParams({ status: x.key })}
          >
            {t(x.label)}
          </button>
        ))}
      </div>
      {rows === null ? (
        <p className="muted">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <EmptyState text={t("empty.listings")} cta={{ label: t("sell.title"), to: "/sell" }} />
      ) : (
        <ul className="list" data-testid="my-listings">
          {rows.map((r) => {
            const seg: Segment | null = ownerSegment(r, now);
            return (
              <li key={r.id} className="list__row">
                <Link
                  to={r.status === "draft" ? `/sell/${r.id}` : `/me/listings/${r.id}`}
                  className="rowlink"
                >
                  <div className="rowlink__cover">
                    {r.cover ? (
                      <img src={`${window.location.origin}/img/${r.cover}`} alt="" />
                    ) : null}
                  </div>
                  <div className="rowlink__body">
                    <div className="rowlink__title">{r.title || t("listing.status.draft")}</div>
                    {seg && r.status === "active" ? (
                      <PriceDisplay segment={seg} size="sm" stagger={tickStagger(r.id)} />
                    ) : null}
                    <div className="hint">
                      {r.status === "active" && r.end_at
                        ? `${formatCountdown(r.end_at - now, locale)} · `
                        : ""}
                      {r.status === "scheduled" && r.start_at
                        ? `${formatDropTime(r.start_at, locale, now)} · `
                        : ""}
                      {r.quantity - r.quantity_remaining}/{r.quantity} · 👁 {r.watch_count} · 🔔{" "}
                      {r.alert_count} · {t(`listing.status.${r.status}`)}
                      {seg
                        ? ` · ${t(`bucket.${bucket({ start_at: r.start_at ?? now, duration_s: r.duration_s ?? 0 }, now)}`)}`
                        : ""}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
